import React, { useCallback } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { getRequestURL } from "@tanstack/react-start/server";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { getCoach, updateCoach } from "~/lib/server/coach";
import {
  createStripeAccount,
  getStripeAccount,
  onboardStripeAccount,
} from "~/lib/stripe/onboarding";
import { Card, CardContent } from "~/components/ui/card";
import {
  AlertTriangle,
  Check,
  Clock,
  Coins,
  DollarSign,
  FileCheck2,
  Link as LinkIcon,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const getRefreshedCoach = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .validator(
    z.object({
      accountId: z.string().optional(),
    }),
  )
  .handler(async ({ data, context }) => {
    const coach = context.coach;

    if (data.accountId) {
      if (coach.stripeAccountId !== data.accountId) {
        return { coach };
      }

      const account = await getStripeAccount({
        data: { accountId: data.accountId },
      });

      return {
        account,
        coach: await updateCoach(coach.id, {
          stripeAccountId: account.id,
          stripeChargesEnabled: account.charges_enabled,
          stripePayoutsEnabled: account.payouts_enabled,
        }),
      };
    }

    return {
      coach,
      account: coach.stripeAccountId
        ? await getStripeAccount({ data: { accountId: coach.stripeAccountId } })
        : undefined,
    };
  });

export const connectCoachToStripe = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(
    z.object({
      coachId: z.string(),
    }),
  )
  .handler(async ({ data, context: _context }) => {
    const coach = await getCoach(data.coachId);
    if (!coach) {
      throw new Error("Coach not found");
    }

    if (!coach.stripeAccountId) {
      const account = await createStripeAccount({
        data: { email: coach.email },
      });
      if (!account.id) {
        throw new Error("Failed to create Stripe account");
      }

      coach.stripeAccountId = account.id;
      await updateCoach(data.coachId, coach);
    }

    const requestUrl = getRequestURL();
    const origin = `${requestUrl.protocol}//${requestUrl.host}`;
    const link = await onboardStripeAccount({
      data: {
        accountId: coach.stripeAccountId,
        origin,
      },
    });
    return {
      success: true,
      url: link.url,
    };
  });

export const Route = createFileRoute("/coach/_backend/stripe-connect/")({
  head: () =>
    createNoIndexMeta(
      "Stripe Connect – Dream Coach Match",
      "Set up payment processing and manage your Stripe account",
    ),
  loader: async ({ context }) => {
    const searchParams = new URLSearchParams(window?.location?.search || "");
    const accountId = searchParams.get("accountId") || undefined;

    await context.queryClient.ensureQueryData({
      queryKey: ["coach-stripe-data", accountId],
      queryFn: () => getRefreshedCoach({ data: { accountId } }),
    });
    return {};
  },
  component: StripeConnectPage,
});

function StripeConnectPage() {
  const searchParams = new URLSearchParams(window?.location?.search || "");
  const accountId = searchParams.get("accountId") || undefined;

  const { data } = useQuery({
    queryKey: ["coach-stripe-data", accountId],
    queryFn: () => getRefreshedCoach({ data: { accountId } }),
  });

  const connectMutation = useMutation({
    mutationFn: (coachId: string) =>
      connectCoachToStripe({ data: { coachId } }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success("Redirecting to Stripe Connect...");
        window.open(response.url, "_blank");
      }
    },
    onError: () => {
      toast.error("Failed to connect to Stripe");
    },
  });

  const startOnboarding = useCallback(() => {
    if (data?.coach?.id) {
      connectMutation.mutate(data.coach.id);
    }
  }, [data?.coach?.id, connectMutation]);

  if (!data) {
    return <div>Loading...</div>;
  }

  const stripeConnected = !!data.coach.stripeAccountId;
  const stripeDetailsSubmitted = data.account?.details_submitted ?? false;

  const stripeVerificationPending = () => {
    const pendingVerification =
      data.account?.requirements?.pending_verification ?? [];
    return 0 < pendingVerification.length;
  };

  const stripeHasCurrentlyDue = () => {
    const currentlyDue = data.account?.requirements?.currently_due ?? [];
    return 0 < currentlyDue.length;
  };

  const stripePastDue = () => {
    const pastDue = data.account?.requirements?.past_due ?? [];
    return 0 < pastDue.length;
  };

  const stripeHasDeadline = () => {
    return !!data.account?.requirements?.current_deadline;
  };

  const stripeDisabledReason = () => {
    return data.account?.requirements?.disabled_reason;
  };

  const stripeChargesActive = !!data.account?.charges_enabled;
  const stripePayoutsActive = !!data.account?.payouts_enabled;

  return (
    <div className="container mx-auto flex flex-col gap-8 px-4 py-8">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl">Payment configuration</h1>
          <p className="text-text-secondary text-sm">
            Configure your payment settings to start accepting payments from
            clients
          </p>
        </div>
        <div className="flex grow-0 flex-col items-end gap-4">
          <Link to="/coach/invoices">
            <Button variant="outline">Go To Invoices</Button>
          </Link>
        </div>
      </div>
      <Card className="max-w-prose">
        <CardContent className="flex flex-col items-stretch gap-4">
          <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
            {data.coach.contractSigned ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <FileCheck2 />
              </div>
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg text-amber-700">
                <AlertTriangle />
              </div>
            )}
            <div className="flex-1">
              <div className="text-text-primary">Sign Your Coach Contract</div>
              <div className="text-text-tertiary text-sm">
                {data.coach.contractSigned
                  ? "You have signed the coach contract"
                  : "Please check your email for the contract we sent you. You need to sign the coach contract before proceeding with payment setup."}
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 ${!data.coach.contractSigned ? "pointer-events-none opacity-50" : ""}`}
          >
            {stripeConnected ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <LinkIcon />
              </div>
            ) : (
              <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100"></div>
            )}
            <div className="flex grow flex-col">
              <h3 className="text-text-primary">Stripe Account Created</h3>
              <p className="text-text-tertiary text-sm">
                {stripeConnected
                  ? "Your Stripe account is created."
                  : "Create a Stripe account to start sending invoices."}
              </p>
            </div>
          </div>

          {!stripeConnected && data.coach.contractSigned && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending
                ? "Connecting..."
                : "Connect with Stripe"}
            </Button>
          )}

          <div
            className={`flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 ${!data.coach.contractSigned ? "pointer-events-none opacity-50" : ""}`}
          >
            {stripeDetailsSubmitted ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <FileCheck2 />
              </div>
            ) : (
              <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100"></div>
            )}
            <div className="flex grow flex-col">
              <h3 className="text-text-primary">Submit your details</h3>
              <p className="text-text-tertiary text-sm">
                {stripeDetailsSubmitted
                  ? "We've received your details."
                  : "Complete your account setup by submitting your details."}
              </p>
            </div>
          </div>

          {stripeConnected && !stripeDetailsSubmitted && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending ? "Loading..." : "Complete onboarding"}
            </Button>
          )}

          <div
            className={`flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 ${!data.coach.contractSigned ? "pointer-events-none opacity-50" : ""}`}
          >
            {!stripeHasCurrentlyDue() ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <ShieldCheck />
              </div>
            ) : (
              <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100"></div>
            )}
            <div className="flex grow flex-col">
              <h3 className="text-text-primary">Additional KYC verification</h3>
              <p className="text-text-tertiary text-sm">
                {stripeHasCurrentlyDue()
                  ? "We'll need some more information to comply with regulations before we can activate payouts."
                  : "All good! We don't need any more information from you."}
              </p>
              {data.account?.requirements?.currently_due &&
                0 < data.account.requirements.currently_due.length && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {data.account.requirements.currently_due.map(
                      (requirement) => (
                        <Badge key={requirement} size="sm" variant="pending">
                          <Clock />
                          {requirement}
                        </Badge>
                      ),
                    )}
                  </div>
                )}
            </div>
          </div>

          <div
            className={`flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 ${!data.coach.contractSigned ? "pointer-events-none opacity-50" : ""}`}
          >
            {stripeChargesActive ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <DollarSign />
              </div>
            ) : (
              <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100"></div>
            )}
            <div className="flex grow flex-col">
              <h3 className="text-text-primary">Charges Enabled</h3>
              <p className="text-text-tertiary text-sm">
                {stripeChargesActive
                  ? "Done! You can now charge your customers."
                  : !stripeDetailsSubmitted
                    ? "First submit your account details to enable charges."
                    : stripePastDue()
                      ? "Some required information is past due. Please provide this to re-enable charges."
                      : stripeVerificationPending()
                        ? "We're verifying your submitted information."
                        : stripeHasCurrentlyDue()
                          ? "Additional information is needed to enable charges."
                          : "Next, we'll enable charges so you can start sending invoices."}
              </p>

              {/* Show past due warning if any */}
              {stripePastDue() && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Past Due Requirements</span>
                  </div>
                  <p className="mb-3 text-sm text-red-600">
                    The following information is past due. Please provide this
                    as soon as possible:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {data.account?.requirements?.past_due?.map(
                      (requirement) => (
                        <Badge
                          key={requirement}
                          size="sm"
                          variant="destructive"
                        >
                          <AlertTriangle />
                          {requirement
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Show currently due requirements */}
              {stripeHasCurrentlyDue() && !stripePastDue() && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-blue-700">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Required Information</span>
                  </div>
                  <p className="mb-3 text-sm text-blue-600">
                    {stripeHasDeadline() ? (
                      <>
                        Please provide the following by{" "}
                        {new Date(
                          data.account!.requirements!.current_deadline!,
                        ).toLocaleDateString()}
                        :
                      </>
                    ) : (
                      "Please provide the following information to enable charges:"
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {data.account?.requirements?.currently_due?.map(
                      (requirement) => (
                        <Badge key={requirement} size="sm" variant="secondary">
                          <LinkIcon />
                          {requirement
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Show verification in progress */}
              {stripeVerificationPending() && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-amber-700">
                    <Clock className="h-5 w-5" />
                    <span className="font-medium">
                      Verification in Progress
                    </span>
                  </div>
                  <p className="text-sm text-amber-600">
                    We're reviewing your submitted information. This usually
                    takes 24-48 hours. You'll receive an email once the
                    verification is complete.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {data.account?.requirements?.pending_verification?.map(
                      (requirement) => (
                        <Badge key={requirement} size="sm" variant="pending">
                          <Clock />
                          {requirement
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                        </Badge>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Show disabled reason if any */}
              {stripeDisabledReason() && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-gray-700">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Account Status</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {"requirements.past_due" === stripeDisabledReason()
                      ? "Your account is temporarily disabled because some required information is past due."
                      : "requirements.pending_verification" ===
                          stripeDisabledReason()
                        ? "Your account is temporarily disabled while we verify your information."
                        : `Your account is currently disabled (${stripeDisabledReason()}). Please contact support if you need help.`}
                  </p>
                </div>
              )}

              {/* Only show active payment methods */}
              {stripeChargesActive && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(data.account?.capabilities ?? {})
                    .filter(([, capability]) => "active" === capability)
                    .map(([key]) => (
                      <Badge key={key} size="sm" variant="affirm">
                        <Check />
                        {key
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ")}
                      </Badge>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons based on state */}
          {stripeConnected && (
            <>
              {!stripeDetailsSubmitted && (
                <Button
                  onClick={startOnboarding}
                  className="w-full sm:w-auto"
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending
                    ? "Loading..."
                    : "Submit Your Account Details"}
                </Button>
              )}
              {stripeDetailsSubmitted &&
                (stripeHasCurrentlyDue() || stripePastDue()) &&
                !stripeVerificationPending() && (
                  <Button
                    onClick={startOnboarding}
                    className="w-full sm:w-auto"
                    variant={stripePastDue() ? "destructive" : "default"}
                    disabled={connectMutation.isPending}
                  >
                    {connectMutation.isPending
                      ? "Loading..."
                      : stripePastDue()
                        ? "Provide Past Due Information"
                        : "Complete Required Information"}
                  </Button>
                )}
            </>
          )}
          <div
            className={`flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 ${!data.coach.contractSigned ? "pointer-events-none opacity-50" : ""}`}
          >
            {stripePayoutsActive ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lg text-lime-700">
                <Coins />
              </div>
            ) : (
              <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100"></div>
            )}
            <div className="flex grow flex-col">
              <h3 className="text-text-primary">Payouts Active</h3>
              <p className="text-text-tertiary text-sm">
                {stripePayoutsActive
                  ? "Done! Invoice payments will be paid out to you."
                  : "Finally, we need to make sure you can receive payments."}
              </p>
            </div>
          </div>

          {stripeChargesActive && !stripePayoutsActive && (
            <Button
              onClick={startOnboarding}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending
                ? "Loading..."
                : "Finish your setup in Stripe"}
            </Button>
          )}

          {stripeConnected && stripeChargesActive && stripePayoutsActive && (
            <Button
              onClick={startOnboarding}
              variant="outline"
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending
                ? "Loading..."
                : "Update payment configuration"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
